import AddIcon from '@mui/icons-material/Add';
import BorderColorOutlinedIcon from '@mui/icons-material/BorderColorOutlined';
import EditIcon from '@mui/icons-material/BorderColorOutlined';
import { Box, Button, Grid, IconButton, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import * as React from 'react';
import { useHistory } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';

import useBatchDelete from '../bg_tasks/useBatchDelete';
import ConfirmationDialog from '../components/ConfirmationDialog';
import FixedLinearProgress from '../components/FixedLinearProgress';
import PageHeading from '../components/PageHeading';
import PageMeta from '../components/PageMeta';
import RecordList from '../components/RecordList';
import { getLicenseeColumn } from '../components/franchiseColumns';
import { ContextStore } from '../store/contextStore';
import { useIsWidthUp } from '../utils';
import DeleteButton from '../components/DeleteButton';
import DeleteIconButton from '../components/DeleteIconButton';
import BusTourListMobileLayout, { BusTourMobileActionButton, BusTourMobileListItem } from './BusTourListMobile';
import BusTourListSearchField, { busTourListToolbarButtonSx, busTourListToolbarRowSx } from './BusTourListSearchField';
import { useBusTourAddLabel } from './useBusTourAddLabel';

const useStyles = makeStyles((theme) => ({
    recordList: {
        height: `calc(${theme.cp.contentHeight} - 6rem) !important`,
        '&.MuiDataGrid-root .MuiDataGrid-cell:focus-within': {
            outline: 'none',
        },
    },
}));

const AttractionList =({ onRefreshRef, ...props }) => {
    const history = useHistory();
    const { hasPerm, isFranchise, licensees, currentLicensee } = React.useContext(ContextStore);
    const classes = useStyles();
    const isSmallScreen = !useIsWidthUp('sm');

    const [syncActive, setSyncActive] = React.useState(false);
    const [lastUpdate, setLastUpdate] = React.useState(null);
    const [selectedIds, setSelectedIds] = React.useState([]);

    React.useEffect(() => {
        if (onRefreshRef) onRefreshRef.current = () => setLastUpdate(new Date().toString());
    }, [onRefreshRef]);
    const urlParams = new URLSearchParams(window.location.search);
    const [query, setQuery] = React.useState(urlParams.get('query') || null);
    const [idsToDelete, setIdsToDelete] = React.useState(null);
    const addButtonLabel = useBusTourAddLabel('NEW ATTRACTION');

    const resource = 'attraction';

    const onQueryChange = (newQuery) => {
        setQuery(newQuery);
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.set('query', newQuery);
        history.push(`${window.location.pathname}?${searchParams.toString()}`);
    };

    // The backend runs attraction batch delete as a background task (202 + task_id), so the rows are
    // only gone once that task finishes (CP-8439); the shared hook follows it (CP-7771).
    const refreshList = React.useCallback(() => setLastUpdate(new Date().toString()), []);
    const { deleteRecords } = useBatchDelete({ resource, names: ['attraction', 'attractions'], onDeleted: refreshList });
    const doDelete = (idlist) => {
        setSyncActive(true);
        deleteRecords(idlist)
            .then((accepted) => {
                if (accepted) {
                    setSelectedIds([]);
                    setIdsToDelete(null);
                }
            })
            .finally(() => setSyncActive(false));
    };

    const listItemRenderer = React.useCallback(
        (row, idx, rows) => {
            const name = row.name || row.display_name || `(attraction ${row.id})`;
            const location = row.latitude && row.longitude ? `${row.latitude}, ${row.longitude}` : '—';
            const openRow = () => history.push(`/bus-tours/attractions/${row.id}`);

            return (
                <BusTourMobileListItem
                    key={row.id}
                    name={name}
                    onOpen={openRow}
                    isLast={idx >= rows.length - 1}
                    details={
                        <>
                            {row.description ? (
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    style={{
                                        wordWrap: 'break-word',
                                        whiteSpace: 'normal',
                                        display: '-webkit-box',
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: 'vertical',
                                        overflow: 'hidden',
                                    }}
                                >
                                    {row.description}
                                </Typography>
                            ) : null}
                            <Typography variant="body2" color="text.secondary" display="block">
                                Category: {row.category?.title || '—'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" display="block">
                                Location: {location}
                            </Typography>
                        </>
                    }
                    actions={
                        <>
                            <Grid item>
                                <IconButton
                                    size="small"
                                    component={RouterLink}
                                    to={`/bus-tours/attractions/${row.id}`}
                                    onClick={(ev) => ev.stopPropagation()}
                                    aria-label="Edit attraction"
                                >
                                    <BorderColorOutlinedIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <DeleteIconButton
                                    title="Delete attraction"
                                    perm="activity.delete"
                                    onClick={(ev) => {
                                        ev.stopPropagation();
                                        setIdsToDelete([row.id]);
                                    }}
                                />
                            </Grid>
                        </>
                    }
                />
            );
        },
        [history]
    );

    const attractionsRecordList = (
        <RecordList
            className={classes.recordList}
            resource={resource}
            disableSelectionOnClick
            sortModel={[{ field: 'name', sort: 'asc' }]}
            deps={lastUpdate}
            query={query}
            selectionModel={selectedIds}
            onSelectionModelChange={(selectionModel, selection) => setSelectedIds(selection || [])}
            listItemRenderer={isSmallScreen ? listItemRenderer : undefined}
            mobileNaturalHeight={isSmallScreen}
            noMatchesLabel="No attractions"
            noItemsFoundMessage="No attractions"
            columns={[
                ...getLicenseeColumn(isFranchise, licensees, currentLicensee),
                {
                    field: 'name',
                    headerName: 'Name',
                    sortable: true,
                    flex: 0.6,
                    valueGetter: (p) => p.row.name || p.row.display_name || `(attraction ${p.row.id})`,
                },
                {
                    field: 'description',
                    headerName: 'Description',
                    sortable: false,
                    flex: 1,
                    valueGetter: (p) => p.row.description || '',
                },
                {
                    field: 'category',
                    headerName: 'Category',
                    sortable: false,
                    width: 170,
                    valueGetter: (p) => p.row.category?.title || '',
                },
                {
                    field: 'location',
                    headerName: 'Location',
                    sortable: false,
                    width: 200,
                    valueGetter: (p) => {
                        if (p.row.latitude && p.row.longitude) {
                            return `${p.row.latitude}, ${p.row.longitude}`;
                        }
                        return '';
                    },
                },
                {
                    field: 'buttons',
                    headerName: 'Action',
                    sortable: false,
                    width: 100,
                    renderCell: (p) => (
                        <Grid container alignItems="center">
                            <Grid item>
                                {/* No stopPropagation here (CP-8408): the grid records this cell on mousedown and
                                    only forgets it when the click reaches document. Swallowed, the stale cell gets
                                    focused on the next click anywhere, which pulls focus out of the edit drawer. */}
                                <IconButton size="small" component={RouterLink} to={`/bus-tours/attractions/${p.id}`}>
                                    <EditIcon fontSize="small" color="primary" />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <DeleteIconButton perm="activity.delete" onClick={() => setIdsToDelete([p.id])} />
                            </Grid>
                        </Grid>
                    ),
                },
            ]}
        />
    );

    return (
        <>
            <PageMeta title="Attractions" />
            {idsToDelete && (
                <ConfirmationDialog
                    keepMounted
                    open={!!idsToDelete}
                    onCancel={() => setIdsToDelete(null)}
                    onConfirm={() => doDelete(idsToDelete)}
                    title={idsToDelete.length === 1 ? 'Delete attraction?' : `Delete ${idsToDelete.length} attractions?`}
                    okButtonText="Delete"
                    cancelButtonText="Cancel"
                >
                    {idsToDelete.length === 1
                        ? 'Are you sure you want to delete this attraction?'
                        : `Are you sure you want to delete ${idsToDelete.length} attractions?`}
                </ConfirmationDialog>
            )}
            <FixedLinearProgress visibility={syncActive} />
            {isSmallScreen ? (
                <BusTourListMobileLayout
                    title="Attractions"
                    searchLabel="Search Attractions"
                    searchValue={query || ''}
                    onSearchChange={(e) => onQueryChange(e.target.value)}
                    actionButton={
                        <BusTourMobileActionButton
                            selectedCount={selectedIds.length}
                            canDelete={hasPerm('activity', 'delete')}
                            onDelete={() => setIdsToDelete(selectedIds)}
                            onAdd={() => history.push('/bus-tours/attractions/new')}
                            addLabel={addButtonLabel}
                            deleteLabel="Delete selected attractions"
                        />
                    }
                >
                    {attractionsRecordList}
                </BusTourListMobileLayout>
            ) : (
                <Grid container direction="row" spacing={2} p={2}>
                    <Grid item xs={12} sx={{ pb: '0 !important' }}>
                        <PageHeading sx={{ mb: 1.5 }}>Attractions</PageHeading>
                    </Grid>
                    <Grid item xs={12} sx={{ pt: '0 !important' }}>
                        <Box sx={busTourListToolbarRowSx}>
                            <BusTourListSearchField
                                value={query || ''}
                                onChange={(e) => onQueryChange(e.target.value)}
                            />
                            {selectedIds.length > 0 ? (
                                <DeleteButton
                                    perm="activity.delete"
                                    onClick={() => setIdsToDelete(selectedIds)}
                                    sx={busTourListToolbarButtonSx}
                                >
                                    {selectedIds.length === 1 ? 'DELETE ATTRACTION' : `DELETE ${selectedIds.length} ATTRACTIONS`}
                                </DeleteButton>
                            ) : (
                                <Button
                                    variant="contained"
                                    startIcon={<AddIcon />}
                                    onClick={() => history.push('/bus-tours/attractions/new')}
                                    sx={busTourListToolbarButtonSx}
                                >
                                    {addButtonLabel}
                                </Button>
                            )}
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        {attractionsRecordList}
                    </Grid>
                </Grid>
            )}
        </>
    );
};

export default AttractionList;
